/* eslint-disable react/jsx-props-no-spreading */
import { observer } from 'mobx-react';
import { FC, useEffect, useMemo } from 'react';
import NotificationsSettingListRow from '../../components/NotificationsSettingListRow/NotificationsSettingListRow';
import NotificationsSettingListRowSkeleton from '../../components/NotificationsSettingListRowSkeleton/NotificationsSettingListRowSkeleton';
import { NotificationsSettingColumnName, NotificationsSettingType } from '../../constants/notifications';
import { INotificationSetting, INotificationsSettingFilters } from '../../types/notifications';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { localeStore } from 'src/mobx/localesStore';
import { IPagination, LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { formatImagePath } from 'src/utils/formatter';
import { emptyArray } from 'src/utils/helpers';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import GridTable, { TGridColumnTitle } from '~/view/containers/GridTable';

interface IProps {
  type: NotificationsSettingType;
  loading: LoadingStatus;
  isLoading: boolean;
  pagination: IPagination<INotificationSetting>;
  filters: INotificationsSettingFilters;
  fetchNotifications: (page: number) => void;
  setFilters: (filters: Partial<INotificationsSettingFilters>) => void;
  onStatusChange: (id: number, status: TBooleanInNumber) => void;
  onDelete: (id: number) => void;
}

const NotificationsSettingList: FC<IProps> = observer(({ loading, isLoading, pagination, ...props }: IProps): JSX.Element => {
  const isNoFound: boolean = !pagination.total && !isLoading;
  const isFirstLoading: boolean = Boolean(isLoading && !pagination.total);

  useEffect(() => {
    props.fetchNotifications(1);
  }, []);

  const columnTitles = useMemo((): TGridColumnTitle<NotificationsSettingColumnName>[] => {
    return [
      { title: 'subject_en', columnName: NotificationsSettingColumnName.SUBJECT_EN },
      { title: 'subject_ar', columnName: NotificationsSettingColumnName.SUBJECT_AR },
      { title: 'text_en', columnName: NotificationsSettingColumnName.TEXT_EN },
      { title: 'text_ar', columnName: NotificationsSettingColumnName.TEXT_AR },
      { title: 'delivery_method', columnName: NotificationsSettingColumnName.DELIVERY_STATUS },
      { title: 'event', columnName: NotificationsSettingColumnName.ORDER_STATUS },
      { title: 'status' },
    ];
  }, []);

  return (
    <div className="d-flex flex-column flex-grow-1">
      {isNoFound ? (
        <div className="d-flex flex-column justify-content-center align-items-center mt-6">
          <img src={formatImagePath(`${Icons.NoResults}.svg`)} alt="no_results_found" className="image__no-found mb-6" />
          <p className="family-medium text-size-24 color-gray2">{localeStore.t('no_results_found')}</p>
        </div>
      ) : (
        <GridTable
          gridTemplateClass="notifications-setting-list-row__grid-template mr-4"
          columnTitles={columnTitles}
          filters={props.filters}
          setFilters={props.setFilters}
        >
          {isFirstLoading
            ? emptyArray().map((_, index) => <NotificationsSettingListRowSkeleton key={'skeleton-' + String(index)} />)
            : pagination.data.map((notification) => <NotificationsSettingListRow key={notification.id} notification={notification} {...props} />)}
        </GridTable>
      )}

      <Pagination type="arrow" pagination={pagination} onChange={props.fetchNotifications} loading={loading} showPaginationInfo showMorePageButtons />
    </div>
  );
});

export default NotificationsSettingList;
